import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  message,
  Tag,
  Tooltip,
  Card,
  Typography,
  Dropdown,
  Empty,
  Space,
  Modal,
  Form,
  Input,
  Select,
  Col,
  InputNumber,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  ReloadOutlined,
  DownOutlined,
  CheckCircleOutlined,
  StopOutlined,
  ExclamationCircleOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import BatchActionToolbar from '../../components/common/BatchActionToolbar';
import { useNavigate } from 'react-router-dom';
import { ColumnsType } from 'antd/es/table';
import FilterPanel from '../../components/common/FilterPanel';
import containerService from '../../services/containerService';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { generateContainerLabelsPDF, ContainerLabelData } from '../../utils/pdfGenerator';
import {
  Container,
  ContainerFilters,
  ContainerPaginationParams,
  ContainerBatchOperation,
  ContainerSortField,
  SortOrder,
} from '../../types/container';
import {
  CONTAINER_MESSAGES,
  CONTAINER_PAGINATION_DEFAULT,
  CONTAINER_STATUS_COLORS,
  CONTAINER_USAGE_STATUS_COLORS,
  CONTAINER_PAGE_SIZES,
  CONTAINER_USAGE_STATUS_OPTIONS,
} from '../../utils/containerConstants';
import { getMockContainerSpecsResponse } from '../../mock/containerData';
import { ContainerSpec } from '../../types/container';
import '../../styles/container.css';

const { confirm } = Modal;
const { Text } = Typography;

const ContainerList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  // State management
  const [loading, setLoading] = useState<boolean>(false);
  const [containers, setContainers] = useState<Container[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filters, setFilters] = useState<ContainerFilters>({});
  const [sortField, setSortField] = useState<ContainerSortField>('gmtModified');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend'>('descend');
  const [printModalVisible, setPrintModalVisible] = useState<boolean>(false);
  const [printLoading, setPrintLoading] = useState<boolean>(false);
  const [printForm] = Form.useForm();
  const [containerSpecs, setContainerSpecs] = useState<ContainerSpec[]>([]);
  const [specsLoading, setSpecsLoading] = useState(false);

  // Load container specifications
  useEffect(() => {
    const loadContainerSpecs = async () => {
      try {
        setSpecsLoading(true);
        const response = getMockContainerSpecsResponse();
        if (response.success && response.data) {
          setContainerSpecs(response.data);
        }
      } catch (error) {
        console.error('Error loading container specs:', error);
      } finally {
        setSpecsLoading(false);
      }
    };

    loadContainerSpecs();
  }, []);

  const fetchContainers = async () => {
    setLoading(true);
    try {
      const response = await containerService.getContainers(
        filters,
        {
          currentPage: current,
          pageSize,
          sortBy: sortField,
          sortOrder: sortOrder === 'ascend' ? 'asc' : 'desc'
        }
      );

      if (response.success && response.data) {
        setContainers(response.data.tableData);
        setTotal(response.data.paging.totalCount);
      } else {
        console.error('返回的数据格式不正确:', response);
        setContainers([]);
        setTotal(0);
      }
    } catch (error) {
      message.error(error.message || '获取托盘列表失败');
      console.error('获取托盘列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContainers();
  }, [current, pageSize, filters, sortField, sortOrder]);

  const handleTableChange = (
    pagination: any,
    _filters: any,
    sorter: any
  ) => {
    setCurrent(pagination.current);
    setPageSize(pagination.pageSize);

    if (sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    }
  };

  const handleFiltersChange = (newFilters: any) => {
    setFilters({ ...filters, ...newFilters });
    setCurrent(1); // 重置到第一页
  };

  const handleCreate = () => {
    navigate('/container/create');
  };

  const handleBatchStatusChange = (newStatus: 'ENABLE' | 'DISABLE') => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('container.selectContainersToUpdate'));
      return;
    }

    const statusText = newStatus === 'ENABLE' ? t('container.enable') : t('container.disable');
    const confirmTitle = newStatus === 'ENABLE' ? t('container.batchEnableConfirm') : t('container.batchDisableConfirm');
    const confirmContent = newStatus === 'ENABLE' 
      ? t('container.confirmEnableSelected', { count: selectedRowKeys.length })
      : t('container.confirmDisableSelected', { count: selectedRowKeys.length });

    confirm({
      title: confirmTitle,
      icon: <ExclamationCircleOutlined />,
      content: confirmContent,
      onOk: async () => {
        try {
          const operation: ContainerBatchOperation = {
            codes: selectedRowKeys as string[],
            operation: newStatus,
          };

          const response = await containerService.batchUpdateContainers(operation);

          if (response.success) {
            const successMessage = newStatus === 'ENABLE' 
              ? t('container.batchEnableSuccess') 
              : t('container.batchDisableSuccess');
            message.success(successMessage);
          } else {
            const failedMessage = newStatus === 'ENABLE' 
              ? t('container.batchEnableFailed') 
              : t('container.batchDisableFailed');
            message.error(failedMessage);
          }

          setSelectedRowKeys([]);
          fetchContainers();
        } catch (error: any) {
          const failedMessage = newStatus === 'ENABLE' 
            ? t('container.batchEnableFailed') 
            : t('container.batchDisableFailed');
          message.error(`${failedMessage}: ${error.message}`);
        }
      },
    });
  };

  const handleBatchPrint = () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t('container.selectToPrint'));
      return;
    }

    setPrintModalVisible(true);
    printForm.setFieldsValue({
      copies: 1
    });
  };

  const handlePrintConfirm = async () => {
    try {
      const values = await printForm.validateFields();
      setPrintLoading(true);

      const selectedContainers = containers.filter(container =>
          selectedRowKeys.includes(container.code)
      );
      const selectedContainersCode = selectedRowKeys;

      const labelData: ContainerLabelData[] = selectedContainers.map(container => ({
        code: container.code,
        copies: values.copies
      }));

      // 生成并下载PDF
      await generateContainerLabelsPDF(
          labelData,
          `container-labels-${new Date().getTime()}.pdf`
      );
      
      // 获取选中容器的编码
      // const selectedContainersCode = selectedRowKeys;
      //
      // await containerService.batchPrintContainers(selectedContainersCode, values.copies);
      
      message.success(t('container.printSuccess'));
      setPrintModalVisible(false);
      setSelectedRowKeys([]);
      fetchContainers();
    } catch (error) {
      console.error('Print failed:', error);
      message.error(t('container.printFailed'));
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePrintCancel = () => {
    setPrintModalVisible(false);
    printForm.resetFields();
  };

  // Format datetime display
  const formatDateTime = (timestamp: number): string => {
    return dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
  };

  // Render status tag
  const renderStatusTag = (status: string, type: 'usage' | 'status') => {
    const colors = type === 'usage' ? CONTAINER_USAGE_STATUS_COLORS : CONTAINER_STATUS_COLORS;
    
    // Use translation keys instead of hard-coded labels
    let labelKey: string;
    if (type === 'usage') {
      labelKey = status === 'ENABLE' ? 'container.enable' : 'container.disable';
    } else {
      labelKey = status === 'ENABLE' ? 'container.enable' : 'container.disable';
    }

    return (
      <Tag color={colors[status as keyof typeof colors]}>
        {t(labelKey)}
      </Tag>
    );
  };

  // Render occupancy status
  const renderOccupancyStatus = (record: Container) => {
    const isOccupied = !!(record.occupantName || record.occupyTaskCode);

    if (isOccupied) {
      return (
        <Tag color="orange">{t('container.occupied')}</Tag>
      );
    }

    return <Tag color="green">{t('container.available')}</Tag>;
  };

  const columns = [
    {
      title: t('container.code'),
      dataIndex: 'code',
      key: 'code',
      // sorter: true,
      // sortOrder: sortField === 'code' ? sortOrder : null,
    },
    {
      title: t('container.containerSpec'),
      dataIndex: 'containerSpecCode',
      key: 'containerSpecCode',
      render: (containerSpecCode: string) => {
        // 通过containerSpecs数组查找匹配的规格名称进行显示
        const spec = containerSpecs.find(spec => spec.code === containerSpecCode);
        return spec ? spec.name : (containerSpecCode || '-');
      },
      // sorter: true,
      // sortOrder: sortField === 'containerSpecName' ? sortOrder : null,
    },

    // {
    //   title: t('container.lastPrintDateTime'),
    //   dataIndex: 'recentPrintTime',
    //   key: 'recentPrintTime',
    //   render: (timestamp: number) => timestamp ? formatDateTime(timestamp) : '-',
    //   sorter: true,
    //   sortOrder: sortField === 'recentPrintDateTime' ? sortOrder : null,
    // },
    {
      title: t('container.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => renderStatusTag(status, 'status'),
      // sorter: true,
      // sortOrder: sortField === 'status' ? sortOrder : null,
    },
    {
      title: t('container.usageStatus'),
      key: 'usageStatus',
      render: (_, record) => renderOccupancyStatus(record),
    },
    {
      title: t('container.occupantName'),
      dataIndex: 'occupantName',
      key: 'occupantName',
      render: (occupantName: string) => occupantName || '-',
    },
    {
      title: t('container.occupyTaskCode'),
      dataIndex: 'occupyTaskCode', 
      key: 'occupyTaskCode',
      render: (occupyTaskCode: string) => occupyTaskCode || '-',
    },
    {
      title: t('common.createTime'),
      dataIndex: 'gmtCreate',
      key: 'gmtCreate',
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === 'gmtCreate' ? sortOrder : null,
    },
    {
      title: t('common.modifyTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      render: (timestamp: number) => formatDateTime(timestamp),
      // sorter: true,
      // sortOrder: sortField === 'gmtModified' ? sortOrder : null,
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  };



  return (
    <div>

      <FilterPanel
          form={form}
          onFilter={(values) => {
            const newFilters = {
              usageStatus: values.usageStatus === 'all' ? undefined : values.usageStatus as any,
              search: values.search?.trim() || undefined,
              containerSpecCode: values.containerSpecCode?.trim() || undefined,
            };
            setFilters(newFilters);
            setCurrent(1);
          }}
          onReset={() => {
            form.resetFields();
            setFilters({});
            setCurrent(1);
            setSortOrder('descend');
          }}
          title={t('menu.container')}
        >
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="search" label={t('common.search')}>
              <Input placeholder={t('container.searchCodeOrName')} allowClear />
            </Form.Item>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="containerSpecCode" label={t('container.containerSpec')}>
              <Select 
                placeholder={t('container.searchContainerSpec')} 
                allowClear
                loading={specsLoading}
              >
                {containerSpecs.map(spec => (
                  <Select.Option key={spec.code} value={spec.code}>
                    {spec.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item name="usageStatus" label={t('container.usageStatus')}>
              <Select placeholder={t('container.selectStatus')} allowClear>
                {CONTAINER_USAGE_STATUS_OPTIONS.map(option => {
                  // Use translation keys for labels instead of hardcoded text
                  let labelKey: string;
                  switch (option.value) {
                    case 'all':
                      labelKey = 'common.all';
                      break;
                    case 'DISABLE':
                      labelKey = 'container.available'; 
                      break;
                    case 'ENABLE':
                      labelKey = 'container.occupied';
                      break;
                    default:
                      labelKey = 'common.unknown';
                  }
                  
                  return (
                    <Select.Option key={option.value} value={option.value}>
                      {t(labelKey)}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </FilterPanel>

      <Card className="table-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <BatchActionToolbar 
            selectedCount={selectedRowKeys.length}
            actions={[
              {
                key: 'print',
                icon: <PrinterOutlined style={{ color: '#1890ff' }} />,
                label: t('container.batchPrint'),
                onClick: handleBatchPrint,
                disabled: loading
              },
              {
                key: 'enable',
                icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
                label: t('container.batchEnable'),
                onClick: () => handleBatchStatusChange('ENABLE'),
                disabled: loading
              },
              {
                key: 'disable',
                icon: <StopOutlined style={{ color: '#faad14' }} />,
                label: t('container.batchDisable'),
                onClick: () => handleBatchStatusChange('DISABLE'),
                disabled: loading
              }
            ]}
            onClearSelection={() => setSelectedRowKeys([])}
          />
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              {t('container.newContainer')}
            </Button>
          </Space>
        </div>

        {containers.length > 0 ? (
          <Table
            rowKey="code"
            rowSelection={rowSelection}
            columns={columns}
            dataSource={containers}
            loading={loading}
            scroll={{ x: 1400, y: 500 }}
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => t('container.totalRecords', { count: total }),
              pageSizeOptions: CONTAINER_PAGE_SIZES.map(size => size.toString()),
            }}
            onChange={handleTableChange}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                {Object.keys(filters).some(key =>
                  filters[key as keyof typeof filters] !== 'all' &&
                  filters[key as keyof typeof filters] !== '' &&
                  filters[key as keyof typeof filters] !== undefined
                )
                  ? t('container.noMatchingContainers')
                  : t('container.noContainerData')}
              </span>
            }
            style={{ margin: '40px 0' }}
          />
        )}
      </Card>
      
      {/* 批量打印确认Modal */}
      <Modal
        title={t('container.printConfirmTitle')}
        open={printModalVisible}
        onOk={handlePrintConfirm}
        onCancel={handlePrintCancel}
        confirmLoading={printLoading}
        okText={t('container.confirmPrint')}
        cancelText={t('common.cancel')}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>{t('container.selectedContainers', { count: selectedRowKeys.length })}</Text>
        </div>
        
        <Form form={printForm} layout="vertical">
          <Form.Item
            name="copies"
            label={t('container.copiesPerContainer')}
            rules={[
              { required: true, message: t('container.copiesRequired') },
              { type: 'number', min: 1, max: 100, message: t('container.copiesRange') }
            ]}
          >
            <InputNumber
              min={1}
              max={100}
              placeholder={t('container.copiesPlaceholder')}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContainerList;